/**
 * BlueprintScreen — Fase 3 · Blueprint técnico (o ARQUITETO decide).
 *
 * Ao entrar já mostra o CATÁLOGO COMPLETO de tecnologias por frente (linguagens,
 * frameworks, infraestrutura, serviços do provedor, bancos) como chips selecionáveis.
 * A IA pode SUGERIR (marca ★ e pré-seleciona), mas TODAS as opções ficam disponíveis
 * para a decisão do arquiteto (greenfield). "Montar/Salvar Blueprint" persiste a stack
 * escolhida + arquitetura e grava na MEMÓRIA do projeto — a execução usa 100% (sem
 * reinventar). Arquivos de referência (infra/arquitetura/compliance/design) são
 * enviados como documentos do projeto (indexados → escopo/execução).
 */
import { C } from '@/theme/palette';
import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjects, useAddProjectDocument } from '@/api/projects';
import { useTechBlueprint, useGenerateBlueprint, useSuggestBlueprintOptions, useBlueprintConflicts, useSaveBlueprint, useDetectedStack, useRecommendServices } from '@/api/techScope';
import { Badge } from '@/components/ui';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

// Catálogo COMPLETO por frente — o arquiteto escolhe de tudo (a IA só recomenda).
export const CATALOG = {
  languages: ['C# .NET 8', 'TypeScript', 'JavaScript', 'Python', 'Java', 'Go', 'PHP', 'Kotlin', 'Rust', 'Ruby', 'Dart', 'SQL'],
  frameworks: ['ASP.NET Core', 'React', 'Next.js', 'Angular', 'Vue 3', 'Node.js', 'NestJS', 'Express', 'Spring Boot', 'Django', 'FastAPI', 'Laravel', '.NET MAUI', 'React Native', 'Flutter', 'Flutter Web',
    'LangChain', 'LangGraph', 'LlamaIndex', 'CrewAI', 'AutoGen', 'Semantic Kernel'],
  databases: ['PostgreSQL', 'MySQL', 'SQL Server', 'Oracle', 'MongoDB', 'Redis', 'Cassandra', 'DynamoDB', 'Elasticsearch',
    'Qdrant', 'Chroma', 'Weaviate', 'Milvus'],
};
// Catálogo POR CAMADA (renderização) — o payload/estado continua achatado em
// sel.languages/sel.frameworks; um chip aqui só entra/sai dessas duas listas.
// Nomes podem se repetir entre camadas (ex.: TypeScript em Web e Backend).
export const LAYERS = [
  { key: 'web', title: 'Frontend Web', color: C.accent,
    languages: ['TypeScript', 'JavaScript', 'Dart'],
    frameworks: ['React', 'Next.js', 'Angular', 'Vue 3', 'Flutter Web'] },
  { key: 'mobile', title: 'Frontend Mobile', color: C.accent,
    languages: ['Kotlin', 'Swift', 'Dart', 'Java'],
    frameworks: ['React Native', 'Flutter', '.NET MAUI'] },
  { key: 'backend', title: 'Backend', color: C.accent,
    languages: ['C# .NET 8', 'Python', 'Java', 'Go', 'PHP', 'Ruby', 'Rust', 'TypeScript', 'JavaScript'],
    frameworks: ['ASP.NET Core', 'FastAPI', 'Django', 'Spring Boot', 'Node.js', 'NestJS', 'Express', 'Laravel'] },
  // Frameworks de agente: a linguagem é a do Backend, por isso a camada não lista linguagens.
  // Os nomes são os que o contrato do blueprint confere pelo uso (`blueprint_contract.py`).
  { key: 'ia', title: 'IA / Agentes', color: C.accent,
    languages: [],
    frameworks: ['LangChain', 'LangGraph', 'LlamaIndex', 'CrewAI', 'AutoGen', 'Semantic Kernel'] },
];
const INFRA_PROVIDERS = ['AWS', 'Azure', 'GCP', 'On-premises'];
const INFRA_SERVICES = {
  AWS: ['AWS Lambda', 'Amazon ECS', 'Amazon EKS', 'Amazon RDS', 'Amazon DynamoDB', 'Amazon S3', 'Amazon SQS', 'Amazon SNS', 'AWS Secrets Manager', 'Amazon API Gateway'],
  Azure: ['Azure App Service', 'Azure Functions', 'Azure Kubernetes Service (AKS)', 'Azure SQL Database', 'Azure Cosmos DB', 'Azure Service Bus', 'Azure Blob Storage', 'Azure Key Vault', 'Azure API Management'],
  GCP: ['Cloud Run', 'Cloud Functions', 'GKE', 'Cloud SQL', 'Firestore', 'Cloud Storage', 'Pub/Sub', 'Secret Manager', 'Apigee'],
  'On-premises': ['Kubernetes (on-prem)', 'PostgreSQL', 'MongoDB', 'RabbitMQ', 'Redis', 'MinIO (S3)', 'HashiCorp Vault', 'NGINX', 'Keycloak', 'Prometheus + Grafana'],
};

// Arquivos de referência (mapeia p/ doc_tag válido do backend).
const REF_TILES = [
  { key: 'infra', label: 'Infraestrutura', hint: 'Topologia, IaC, Helm (YAML, TF, PDF, PNG)', tag: 'architecture' },
  { key: 'arch', label: 'Arquitetura', hint: 'C4, ADRs, diagramas (MD, PDF, PNG, DRAWIO)', tag: 'architecture' },
  { key: 'compliance', label: 'Compliance', hint: 'LGPD, normas, checklists (PDF, DOCX, MD)', tag: 'compliance' },
  { key: 'design', label: 'Design System', hint: 'Tokens, guia de estilo (FIG, MD, PNG, JSON)', tag: 'ux' },
];
const TEXT_EXT = /\.(md|txt|json|ya?ml|tf|csv|xml|html?)$/i;
function readText(file) {
  return new Promise((resolve) => {
    if (!TEXT_EXT.test(file.name) || file.size > 1_000_000) return resolve(null);
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || '').slice(0, 1_000_000));
    r.onerror = () => resolve(null);
    r.readAsText(file);
  });
}

/** Chip selecionável (multi ou single) com marca ★ de recomendado.
 *  `locked` = somente leitura (brownfield): não clicável, estilo esmaecido. */
function Chip({ label, on, recommended, mandatory, reason, onClick, color = C.accent, locked = false }) {
  // "por escopo" (mandatory) tem prioridade visual sobre "recomendado" (★).
  const HOT = '#FF7A00';
  const title = locked ? 'Definido pela stack existente (não editável)'
    : mandatory ? `Incluído por DETERMINAÇÃO do escopo — ${reason || 'exigido pelo escopo'}`
    : recommended ? 'Recomendado pela IA' : undefined;
  return (
    <button type="button" disabled={locked} onClick={locked ? undefined : onClick} title={title}
      style={{ cursor: locked ? 'default' : 'pointer', borderRadius: 999, padding: '6px 12px', fontSize: 12.5,
        border: `1px solid ${mandatory ? HOT : (on ? color : C.border)}`,
        background: on ? (mandatory ? `${HOT}22` : `${color}22`) : 'transparent',
        color: on ? C.text : C.dim, display: 'inline-flex', alignItems: 'center', gap: 6,
        opacity: locked && !on ? 0.5 : 1 }}>
      {mandatory
        ? <span style={{ color: HOT, fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>⚓ ESCOPO</span>
        : (recommended && <span style={{ color }}>★</span>)}
      {label}
    </button>
  );
}

function Front({ title, color, options, selected, recommended, mandatory, reasons, onToggle, locked = false }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16 }}>
      <h4 style={{ margin: '0 0 12px', fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
        letterSpacing: '0.1em', color }}>{title}</h4>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map((opt) => (
          <Chip key={opt} label={opt} color={color} locked={locked}
            on={Array.isArray(selected) ? selected.includes(opt) : selected === opt}
            recommended={recommended?.has(opt)} mandatory={mandatory?.has(opt)}
            reason={reasons?.[opt]} onClick={() => onToggle(opt)} />
        ))}
        {options.length === 0 && <span style={{ color: C.muted, fontSize: 12 }}>—</span>}
      </div>
    </div>
  );
}

/** Card de uma CAMADA (Frontend Web/Mobile/Backend) com duas sub-seções:
 *  Linguagens e Frameworks. O `on`/toggle de cada chip opera sobre as listas
 *  achatadas sel.languages/sel.frameworks — a camada é só organização visual. */
function LayerCard({ title, color, languages, langSelected, langRecommended, onToggleLang,
  frameworks, fwSelected, fwRecommended, onToggleFw, locked = false }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16 }}>
      <h4 style={{ margin: '0 0 12px', fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
        letterSpacing: '0.1em', color }}>{title}</h4>
      {languages.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Linguagens</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {languages.map((opt) => (
              <Chip key={opt} label={opt} color={color} locked={locked}
                on={langSelected.includes(opt)} recommended={langRecommended?.has(opt)}
                onClick={() => onToggleLang(opt)} />
            ))}
          </div>
        </div>
      )}
      <div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Frameworks</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {frameworks.map((opt) => (
            <Chip key={opt} label={opt} color={color} locked={locked}
              on={fwSelected.includes(opt)} recommended={fwRecommended?.has(opt)}
              onClick={() => onToggleFw(opt)} />
          ))}
          {frameworks.length === 0 && <span style={{ color: C.muted, fontSize: 12 }}>—</span>}
        </div>
      </div>
    </div>
  );
}

function RefTile({ tile, files, onFiles, onRemove }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, background: C.surface }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{tile.label}</span>
        {files.length > 0 && <span style={{ fontSize: 11, color: C.accent }}>· {files.length}</span>}
      </div>
      <label style={{ display: 'block', border: `2px dashed ${C.border}`, borderRadius: 8, padding: 14,
        textAlign: 'center', cursor: 'pointer', color: C.dim, fontSize: 12 }}>
        ⬆ {tile.hint}
        <input type="file" multiple style={{ display: 'none' }}
          onChange={(e) => { onFiles(tile, e.target.files); e.target.value = ''; }} />
      </label>
      {files.map((f, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: 6, padding: '5px 8px', background: C.surface2, borderRadius: 6, fontSize: 12 }}>
          <span style={{ color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {f.name}</span>
          <button onClick={() => onRemove(tile.key, i)} style={{ color: C.muted, cursor: 'pointer', background: 'none', border: 'none' }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function Group({ title, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16 }}>
      <h4 style={{ margin: '0 0 10px', fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
        letterSpacing: '0.1em', color: color || C.dim }}>{title}</h4>
      <ul style={{ margin: 0, paddingLeft: 16, color: C.dim, fontSize: 13 }}>
        {items.map((it, i) => <li key={i} style={{ margin: '4px 0' }}>{it}</li>)}
      </ul>
    </div>
  );
}

function uniq(arr) { return [...new Set(arr.filter(Boolean))]; }

// Brownfield: papel do repo → rótulo pt-BR + cor (tokens existentes da paleta C local).
const ROLE_META = {
  frontend: { label: 'Front-end',      color: C.accent },
  backend:  { label: 'Back-end',       color: C.quantum },
  database: { label: 'Banco de dados', color: C.warn },
  infra:    { label: 'Infra',          color: C.dim },
  unknown:  { label: 'Indefinido',     color: C.muted },
};
const roleMeta = (r) => ROLE_META[r] || ROLE_META.unknown;
const shortRepo = (r) => String(r || '').split('/').pop() || String(r || '');
// "TypeScript (React/JSX)" → "TypeScript" — remove o parêntese de variante de exibição.
const stripVariant = (s) => String(s || '').replace(/\s*\(.*?\)\s*/g, '').trim();

export function BlueprintScreen({ projectId: wizardProjectId, saveRef } = {}) {
  const [searchParams] = useSearchParams();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = wizardProjectId || urlProject || projects?.[0]?.id || null;

  const { data: saved } = useTechBlueprint(projectId);
  const detected = useDetectedStack(projectId);
  // Só travamos como brownfield quando REALMENTE detectamos repositórios; sem repos,
  // mantém o comportamento editável atual (mesmo que projectType diga "brownfield").
  // A trava só vale quando a detecção ENCONTROU a stack: repositório sem nada reconhecido
  // não pode deixar a tela travada e vazia (só a IA conseguia preencher — e o palpite
  // virava contrato do codegen).
  const detectouStack = ((detected.data?.languages?.length ?? 0)
    + (detected.data?.frameworks?.length ?? 0) + (detected.data?.databases?.length ?? 0)) > 0;
  const isBrownfield = detected.data?.brownfield !== false
    && (detected.data?.repos?.length ?? 0) > 0 && detectouStack;
  const suggest = useSuggestBlueprintOptions(projectId);
  const recommendSvc = useRecommendServices(projectId);
  const generate = useGenerateBlueprint(projectId);
  const conflicts = useBlueprintConflicts(projectId);
  const addDoc = useAddProjectDocument(projectId);
  const saveBlueprint = useSaveBlueprint(projectId);

  // Catálogo (pode ganhar opções extras vindas da IA) + recomendados (★).
  const [langOpts, setLangOpts] = useState(CATALOG.languages);
  const [fwOpts, setFwOpts] = useState(CATALOG.frameworks);
  const [dbOpts, setDbOpts] = useState(CATALOG.databases);
  // Serviços extras recomendados pela IA que não estão no catálogo curado do
  // provedor selecionado (catch-all, para não sumirem da UI).
  const [svcOpts, setSvcOpts] = useState([]);
  // Serviços incluídos por DETERMINAÇÃO do escopo (⚓) + o porquê — diferencia de recomendação.
  const [mandSvc, setMandSvc] = useState(new Set());
  const [mandReasons, setMandReasons] = useState({});
  const [rec, setRec] = useState({ languages: new Set(), frameworks: new Set(), databases: new Set(), infrastructure: new Set(), services: new Set() });
  const [sel, setSel] = useState({ languages: [], frameworks: [], infrastructure: '', services: [], databases: [] });
  const [refFiles, setRefFiles] = useState({ infra: [], arch: [], compliance: [], design: [] });
  const [initDone, setInitDone] = useState(false);
  const [bfInit, setBfInit] = useState(false);

  // Pré-carrega a seleção a partir de um blueprint já salvo (uma vez).
  useEffect(() => {
    if (initDone || !saved) return;
    const infraList = saved.infrastructure ?? [];
    const provider = infraList.find((x) => INFRA_PROVIDERS.includes(x)) || '';
    const services = infraList.filter((x) => x !== provider);
    setSel({
      languages: saved.languages ?? [], frameworks: saved.frameworks ?? [],
      infrastructure: provider, services, databases: saved.databases ?? [],
    });
    setLangOpts(uniq([...CATALOG.languages, ...(saved.languages ?? [])]));
    setFwOpts(uniq([...CATALOG.frameworks, ...(saved.frameworks ?? [])]));
    setDbOpts(uniq([...CATALOG.databases, ...(saved.databases ?? [])]));
    setInitDone(true);
  }, [saved, initDone]);

  // Brownfield: pré-preenche as LINGUAGENS a partir da stack detectada nos repos e
  // trava a edição. Roda UMA vez (flag bfInit, no espírito do initDone) para não
  // sobrescrever a seleção a cada render nem clobber um blueprint já salvo.
  useEffect(() => {
    if (bfInit || !isBrownfield || !detected.data) return;
    const langs = uniq((detected.data.languages ?? []).map(stripVariant));
    const fws = uniq(detected.data.frameworks ?? []);
    const dbs = uniq(detected.data.databases ?? []);
    setSel((p) => ({
      ...p,
      languages: uniq([...p.languages, ...langs]),
      frameworks: uniq([...p.frameworks, ...fws]),
      databases: uniq([...p.databases, ...dbs]),
    }));
    if (langs.length) setLangOpts((o) => uniq([...o, ...langs]));
    if (fws.length) setFwOpts((o) => uniq([...o, ...fws]));
    if (dbs.length) setDbOpts((o) => uniq([...o, ...dbs]));
    setBfInit(true);
  }, [isBrownfield, detected.data, bfInit]);

  // Registra o "save" para o botão CONTINUAR do wizard: ao avançar, persiste TODA a
  // seleção atual (stack + serviços + arquitetura) e grava na MEMÓRIA — nada fica de
  // fora; a execução obtém 100% sem reinventar. (Os arquivos de referência já vão à
  // memória no upload.)
  useEffect(() => {
    if (!saveRef) return undefined;
    saveRef.current = async () => {
      const n = sel.languages.length + sel.frameworks.length + sel.databases.length
        + (sel.infrastructure ? 1 : 0) + sel.services.length;
      if (n === 0) return;
      const infrastructure = uniq([sel.infrastructure, ...sel.services]);
      try {
        await saveBlueprint.mutateAsync({
          languages: sel.languages, frameworks: sel.frameworks,
          infrastructure, databases: sel.databases,
          // generate.data (POST /generate) é camelCase; saved (GET /tech-blueprint)
          // é snake_case — por isso os dois nomes ao ler o fallback.
          target_architecture: generate.data?.targetArchitecture || saved?.target_architecture || '',
          selected_services: sel.infrastructure ? { [sel.infrastructure]: sel.services } : {},
          selected_artifacts: generate.data?.artifacts || saved?.selected_artifacts || [],
        });
      } catch { /* não bloqueia a navegação se falhar */ }
    };
    return () => { if (saveRef) saveRef.current = null; };
  }, [saveRef, sel, generate.data, saved]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  const toggleMulti = (key, setOpts) => (opt) => setSel((p) => {
    const cur = p[key] || [];
    return { ...p, [key]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });
  const toggleService = (opt) => setSel((p) => ({ ...p, services: p.services.includes(opt) ? p.services.filter((x) => x !== opt) : [...p.services, opt] }));

  // Recomenda/auto-seleciona os serviços da cloud escolhida, coerentes com o ESCOPO +
  // as escolhas de frontend/backend/banco. Usa a seleção ATUAL (via setSel callback p/
  // pegar o estado fresco) e pré-seleciona os recomendados retornados pela IA.
  async function recommendServicesFor(cloud, cur) {
    if (!cloud || !INFRA_PROVIDERS.includes(cloud)) return;
    try {
      const res = await recommendSvc.mutateAsync({
        infrastructure: cloud, languages: cur.languages, frameworks: cur.frameworks,
        databases: cur.databases,
      });
      const list = res.services || [];
      const all = list.map((o) => o.name);
      const recNames = list.filter((o) => o.recommended).map((o) => o.name);
      const mandNames = list.filter((o) => o.mandatory).map((o) => o.name);
      const mandReasonMap = {};
      list.filter((o) => o.mandatory).forEach((o) => { mandReasonMap[o.name] = o.reason || ''; });
      const curated = INFRA_SERVICES[cloud] || [];
      const extra = all.filter((n) => !curated.includes(n));
      if (extra.length) setSvcOpts((o) => uniq([...o, ...extra]));
      setRec((r) => ({ ...r, services: new Set([...(r.services || []), ...recNames]) }));
      setMandSvc(new Set(mandNames));
      setMandReasons(mandReasonMap);
      // Auto-seleciona recomendados + obrigatórios (por escopo).
      setSel((p) => (p.infrastructure === cloud
        ? { ...p, services: uniq([...p.services, ...recNames, ...mandNames]) }
        : p));
    } catch { /* erro exibido pelo MutationError global; mantém a escolha manual */ }
  }

  // Escolher a cloud → limpa serviços e DISPARA a recomendação contextual da IA.
  const chooseInfra = (opt) => setSel((p) => {
    const next = { ...p, infrastructure: p.infrastructure === opt ? '' : opt, services: [] };
    if (next.infrastructure) {
      // dispara fora do updater (com o estado que vai valer).
      setTimeout(() => recommendServicesFor(next.infrastructure, next), 0);
    }
    return next;
  });
  const setInfra = chooseInfra;

  async function suggestWithAI() {
    try {
      const res = await suggest.mutateAsync();
      const pick = (arr) => (arr || []).map((o) => o.name);
      const recNames = (arr) => new Set((arr || []).filter((o) => o.recommended).map((o) => o.name));
      setLangOpts((o) => uniq([...o, ...pick(res.languages)]));
      setFwOpts((o) => uniq([...o, ...pick(res.frameworks)]));
      setDbOpts((o) => uniq([...o, ...pick(res.databases)]));
      const recInfra = (res.infrastructure || []).filter((o) => o.recommended).map((o) => o.name);
      const recServiceNames = recNames(res.services);
      setRec({ languages: recNames(res.languages), frameworks: recNames(res.frameworks),
        databases: recNames(res.databases), infrastructure: new Set(recInfra), services: recServiceNames });
      // Provedor a considerar p/ serviços: o já selecionado ou o que a IA recomendou.
      const providerForServices = sel.infrastructure || recInfra.find((x) => INFRA_PROVIDERS.includes(x)) || '';
      if (providerForServices) {
        const curated = INFRA_SERVICES[providerForServices] || [];
        const svcExtra = pick(res.services).filter((n) => !curated.includes(n));
        if (svcExtra.length) setSvcOpts((o) => uniq([...o, ...svcExtra]));
      }
      // pré-seleciona os recomendados (união com o que já estava escolhido). Projeto
      // existente: linguagens, frameworks e bancos vêm do repositório — a IA só
      // complementa infraestrutura e serviços.
      const recomendados = (arr) => (arr || []).filter((o) => o.recommended).map((o) => o.name);
      setSel((p) => ({
        ...p,
        languages: isBrownfield ? p.languages : uniq([...p.languages, ...recomendados(res.languages)]),
        frameworks: isBrownfield ? p.frameworks : uniq([...p.frameworks, ...recomendados(res.frameworks)]),
        databases: isBrownfield ? p.databases : uniq([...p.databases, ...recomendados(res.databases)]),
        infrastructure: p.infrastructure || recInfra.find((x) => INFRA_PROVIDERS.includes(x)) || p.infrastructure,
        services: providerForServices
          ? uniq([...p.services, ...(res.services || []).filter((o) => o.recommended).map((o) => o.name)])
          : p.services,
      }));
    } catch { /* erro abaixo */ }
  }

  // "Montar/Salvar Blueprint": (1) monta a arquitetura + PERSISTE + grava na MEMÓRIA;
  // (2) verifica conflitos IMEDIATAMENTE, sobre o blueprint recém-salvo + TUDO na
  // memória (escopo, docs, referências). Rodar aqui (e não num botão à parte) evita
  // disparar a análise fora de sincronia.
  async function save() {
    const infrastructure = uniq([sel.infrastructure, ...sel.services]);
    try {
      await generate.mutateAsync({ languages: sel.languages, frameworks: sel.frameworks, infrastructure, databases: sel.databases });
      await conflicts.mutateAsync();
    } catch { /* erros exibidos abaixo */ }
  }

  async function onFiles(tile, fileList) {
    const arr = Array.from(fileList || []);
    for (const f of arr) {
      const content_text = await readText(f);
      addDoc.mutate({ filename: `[${tile.label}] ${f.name}`, doc_tag: tile.tag,
        size_bytes: f.size, content_type: f.type || null, content_text, storage_url: '' });
    }
    setRefFiles((p) => ({ ...p, [tile.key]: [...p[tile.key], ...arr.map((f) => ({ name: f.name }))] }));
  }
  const removeRef = (key, i) => setRefFiles((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));

  const total = sel.languages.length + sel.frameworks.length + sel.databases.length + (sel.infrastructure ? 1 : 0) + sel.services.length;
  const built = generate.data;
  const infraServiceOpts = sel.infrastructure ? uniq([...(INFRA_SERVICES[sel.infrastructure] || []), ...svcOpts]) : [];

  // Catch-all: linguagens/frameworks vindos da IA/brownfield/saved que não pertencem
  // a NENHUMA camada aparecem no card Backend, para nada sumir da UI.
  const allLayerLangs = uniq(LAYERS.flatMap((l) => l.languages));
  const allLayerFws = uniq(LAYERS.flatMap((l) => l.frameworks));
  const extraLangs = langOpts.filter((l) => !allLayerLangs.includes(l));
  const extraFws = fwOpts.filter((f) => !allLayerFws.includes(f));

  return (
    <div className="sp-page" style={{ color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>Blueprint técnico</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={suggestWithAI} disabled={suggest.isPending}
            style={{ cursor: suggest.isPending ? 'default' : 'pointer', background: C.quantum, color: '#fff',
              border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, opacity: suggest.isPending ? 0.6 : 1 }}>
            {suggest.isPending ? 'Sugerindo…' : '✨ Sugerir com IA'}
          </button>
          <button onClick={save} disabled={generate.isPending || conflicts.isPending || total === 0}
            title="Monta a arquitetura, salva na memória e verifica conflitos — tudo em sincronia"
            style={{ cursor: (generate.isPending || conflicts.isPending || total === 0) ? 'default' : 'pointer', background: C.accent,
              color: C.black, border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700,
              opacity: (generate.isPending || conflicts.isPending || total === 0) ? 0.5 : 1 }}>
            {generate.isPending ? 'Montando…' : conflicts.isPending ? 'Verificando conflitos…' : '🏗 Montar/Salvar + Verificar'}
          </button>
        </div>
      </div>
      <p style={{ color: C.dim, maxWidth: '80ch', marginTop: 10, fontSize: 13.5 }}>
        <strong style={{ color: C.text }}>Você (arquiteto) decide a stack</strong> — escolha em cada frente abaixo (todas as
        opções disponíveis). A IA <strong style={{ color: C.quantum }}>recomenda ★</strong> e pode pré-selecionar, mas a decisão
        é sua. <strong style={{ color: C.text }}>"Montar/Salvar + Verificar"</strong> persiste a stack, grava na
        <strong style={{ color: C.text }}> memória do projeto</strong> e roda a <strong style={{ color: C.warn }}>verificação de
        conflitos</strong> no mesmo passo (sobre tudo que está na memória: escopo, docs e referências) — a
        geração de código usa 100% o que for definido aqui.
      </p>

      {(suggest.isError || generate.isError) && (
        <div style={{ marginTop: 12, color: C.quantum, fontSize: 13 }}>
          Falha: {String((suggest.error || generate.error)?.title || (suggest.error || generate.error)?.message || suggest.error || generate.error)}
        </div>
      )}

      {/* Brownfield: stack existente (read-only) — vem dos repositórios conectados */}
      {isBrownfield && (
        <div style={{ marginTop: 16, border: `1px solid ${C.warn}55`, borderRadius: 12, padding: 16, background: C.surface }}>
          <h4 style={{ margin: '0 0 12px', fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
            letterSpacing: '0.1em', color: C.warn }}>Stack existente — detectada dos repositórios (não editável)</h4>
          <div style={{ display: 'grid', gap: 8 }}>
            {(detected.data.repos ?? []).map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                padding: '8px 10px', background: C.surface2, borderRadius: 8 }}>
                <Badge color={roleMeta(r.role).color} light>{roleMeta(r.role).label}</Badge>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{shortRepo(r.repo)}</span>
                <span style={{ fontSize: 12.5, color: C.dim }}>{(r.languages ?? []).join(', ')}</span>
              </div>
            ))}
          </div>
          <p style={{ margin: '10px 0 0', color: C.muted, fontSize: 12 }}>
            Projeto existente: a stack vem dos repositórios já conectados e não pode ser trocada. A IA apenas confirma e complementa.
          </p>
        </div>
      )}

      {/* Catálogo de tecnologias POR CAMADA (greenfield: o arquiteto escolhe; brownfield: travado).
          Cada camada tem sub-seções Linguagens/Frameworks; um chip entra/sai das listas
          achatadas sel.languages/sel.frameworks — o mesmo nome pode existir em >1 camada. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14, marginTop: 16 }}>
        {LAYERS.map((layer) => (
          <LayerCard key={layer.key} title={layer.title} color={layer.color}
            languages={layer.key === 'backend' ? uniq([...layer.languages, ...extraLangs]) : layer.languages}
            frameworks={layer.key === 'backend' ? uniq([...layer.frameworks, ...extraFws]) : layer.frameworks}
            langSelected={sel.languages} langRecommended={rec.languages} onToggleLang={toggleMulti('languages')}
            fwSelected={sel.frameworks} fwRecommended={rec.frameworks} onToggleFw={toggleMulti('frameworks')}
            locked={isBrownfield} />
        ))}
        <Front title="Bancos de dados" color={C.quantum} options={dbOpts} selected={sel.databases} locked={isBrownfield}
          recommended={rec.databases} onToggle={toggleMulti('databases')} />
        <Front title="Infraestrutura (provedor)" color={C.quantum} options={INFRA_PROVIDERS} selected={sel.infrastructure} locked={isBrownfield}
          recommended={rec.infrastructure} onToggle={setInfra} />
      </div>
      {sel.infrastructure && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: C.dim }}>
              A IA já selecionou (★) os serviços de <strong style={{ color: C.text }}>{sel.infrastructure}</strong> coerentes
              com o <strong style={{ color: C.text }}>escopo</strong> e a sua stack. Ajuste se quiser.
            </span>
            {!isBrownfield && (
              <button type="button"
                onClick={() => recommendServicesFor(sel.infrastructure, sel)}
                disabled={recommendSvc.isPending}
                style={{ marginLeft: 'auto', cursor: recommendSvc.isPending ? 'default' : 'pointer',
                  background: 'transparent', color: C.accent, border: `1px solid ${C.accent}55`,
                  borderRadius: 7, padding: '5px 11px', fontSize: 12, fontWeight: 600 }}>
                {recommendSvc.isPending ? 'Selecionando…' : '↻ Recomendar serviços com IA'}
              </button>
            )}
          </div>
          <Front title={`Serviços de ${sel.infrastructure}`} color={C.quantum} options={infraServiceOpts} locked={isBrownfield}
            selected={sel.services} recommended={rec.services} mandatory={mandSvc} reasons={mandReasons}
            onToggle={toggleService} />
          {mandSvc.size > 0 && (
            <div style={{ marginTop: 8, fontSize: 11.5, color: C.dim }}>
              <span style={{ color: '#FF7A00', fontWeight: 700 }}>⚓ ESCOPO</span> = incluído por
              determinação do escopo (não é recomendação nem escolha aleatória). Passe o mouse para ver o porquê.
            </div>
          )}
          {recommendSvc.data?.rationale && (
            <div style={{ marginTop: 8, fontSize: 12, color: C.dim, lineHeight: 1.5,
              borderLeft: `2px solid ${C.accent}55`, paddingLeft: 10 }}>
              {recommendSvc.data.rationale}
            </div>
          )}
        </div>
      )}
      <div style={{ marginTop: 12, color: C.muted, fontSize: 12 }}>{total} item(ns) escolhido(s) pelo arquiteto.</div>

      {/* Arquivos de referência → documentos do projeto (escopo/execução) */}
      <div style={{ marginTop: 22 }}>
        <h4 style={{ margin: '0 0 10px', fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
          letterSpacing: '0.1em', color: C.accent }}>Arquivos de referência (infra · arquitetura · compliance · design)</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {REF_TILES.map((t) => (
            <RefTile key={t.key} tile={t} files={refFiles[t.key]} onFiles={onFiles} onRemove={removeRef} />
          ))}
        </div>
        {addDoc.isError && <div style={{ marginTop: 8, color: C.warn, fontSize: 12 }}>Falha ao enviar referência.</div>}
      </div>

      {/* Conflitos — verificados NO fluxo de Montar/Salvar (sobre tudo na memória) */}
      {conflicts.data && (
        <div style={{ marginTop: 18, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, background: C.surface }}>
          <h4 style={{ margin: '0 0 10px', fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
            letterSpacing: '0.1em', color: conflicts.data.conflicts?.length ? C.warn : C.accent }}>
            {conflicts.data.conflicts?.length
              ? `Conflitos encontrados (${conflicts.data.conflicts.length}) · considerou escopo + referências`
              : 'Nenhum conflito ✓ · verificado sobre escopo + referências'}
          </h4>
          {(conflicts.data.conflicts || []).map((c, i) => (
            <div key={i} style={{ borderTop: i ? `1px solid ${C.border}` : 'none', padding: '8px 0' }}>
              <strong style={{ fontSize: 13 }}>[{c.severity}] {c.title}</strong>
              {c.detail && <p style={{ color: C.dim, fontSize: 12.5, margin: '4px 0 0' }}>{c.detail}</p>}
              {c.suggestion && <p style={{ color: C.text, fontSize: 12.5, margin: '4px 0 0' }}>→ {c.suggestion}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Blueprint montado + salvo (arquitetura + artefatos) */}
      {built && (
        <div style={{ marginTop: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Blueprint salvo</h3>
            <span style={{ fontSize: 11, color: C.accent }}>✓ na memória do projeto</span>
          </div>
          {built.targetArchitecture && (
            <div style={{ border: `1px solid ${C.border}`, background: C.surface2, borderRadius: 12, padding: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
                letterSpacing: '0.1em', color: C.accent }}>Arquitetura alvo (montada pela IA a partir da sua escolha)</h4>
              <p style={{ margin: 0, color: C.text, fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{built.targetArchitecture}</p>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14, marginTop: 14 }}>
            <Group title="Linguagens" items={built.languages} color={C.accent} />
            <Group title="Frameworks" items={built.frameworks} color={C.accent} />
            <Group title="Infraestrutura" items={built.infrastructure} color={C.quantum} />
            <Group title="Bancos de dados" items={built.databases} color={C.quantum} />
            <Group title="Artefatos a gerar" items={built.artifacts} color={C.dim} />
          </div>
        </div>
      )}
    </div>
  );
}
